import { useTranslation } from "react-i18next";
import { CalculatorResults as Results } from "./types";
import { formatCurrency } from "./calculations";
import ScrollReveal from "@/components/ScrollReveal";

interface CalculatorResultsProps {
  results: Results;
}

const CalculatorResults = ({ results }: CalculatorResultsProps) => {
  const { t } = useTranslation();

  const isNegativeSavings = results.directSavings < 0;

  return (
    <ScrollReveal delay={0.2}>
      <div className="space-y-6">
        {/* Results Card */}
        <div className="bg-card rounded-lg border border-border p-8 shadow-sm space-y-6">
          <h3 className="text-xl font-bold text-foreground mb-6">
            {t("corporateCalculator.calculator.title", {
              defaultValue: "Cost Comparison Results",
            })}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Hotel Cost */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                {t("corporateCalculator.calculator.results.hotelCost")}
              </p>
              <p className="text-3xl font-bold text-foreground">
                {formatCurrency(results.hotelCost)} SEK
              </p>
            </div>

            {/* Guestly Cost */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                {t("corporateCalculator.calculator.results.guestlyCost")}
              </p>
              <p className="text-3xl font-bold text-foreground">
                {formatCurrency(results.guestlyCost)} SEK
              </p>
            </div>

            {/* Direct Savings */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                {t("corporateCalculator.calculator.results.directSavings")}
              </p>
              <p
                className={`text-3xl font-bold ${
                  isNegativeSavings ? "text-destructive" : "text-primary"
                }`}
              >
                {isNegativeSavings ? "-" : "+"}
                {formatCurrency(Math.abs(results.directSavings))} SEK
              </p>
            </div>

            {/* Gym Value */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                {t("corporateCalculator.calculator.results.gymValue")}
              </p>
              <p className="text-3xl font-bold text-primary">
                +{formatCurrency(results.gymValue)} SEK
              </p>
            </div>
          </div>

          {/* Total Advantage */}
          <div className="bg-primary/10 border-2 border-primary rounded-lg p-6 mt-6">
            <p className="text-sm text-muted-foreground mb-2">
              {t("corporateCalculator.calculator.results.totalAdvantage")}
            </p>
            <p className="text-4xl font-bold text-primary">
              {results.totalAdvantage >= 0 ? "+" : "-"}
              {formatCurrency(Math.abs(results.totalAdvantage))} SEK
            </p>
          </div>
        </div>

        {/* Narrative Explanation */}
        <div className="bg-muted/30 rounded-lg p-6 space-y-3 text-muted-foreground">
          <p>
            {isNegativeSavings
              ? t("corporateCalculator.calculator.narrative.negativeSavings", {
                  amount: formatCurrency(Math.abs(results.directSavings)),
                })
              : t("corporateCalculator.calculator.narrative.savings", {
                  amount: formatCurrency(results.directSavings),
                })}
          </p>
          {results.gymValue > 0 && (
            <p>
              {t("corporateCalculator.calculator.narrative.gym", {
                amount: formatCurrency(results.gymValue),
              })}
            </p>
          )}
          <p>{t("corporateCalculator.calculator.narrative.impact")}</p>
        </div>
      </div>
    </ScrollReveal>
  );
};

export default CalculatorResults;
